import type { FundingInstabilityRegime, NormalizedLeaderboardRow } from "@/lib/analytics-transforms";
import { formatFunding, formatFundingBps, formatPercent, formatSpreadBps } from "@/lib/formatters";
import { useNavigate } from "react-router-dom";

type LeaderboardTableProps = {
  title: string;
  rows: NormalizedLeaderboardRow[];
  valueHeader?: string;
  subtitle?: string;
  isLoading?: boolean;
  isUpdating?: boolean;
};

function formatMetric(metric: string, value: number | null) {
  if (value == null) return "—";
  if (metric === "spreadTightest" || metric === "spreadWidest") return formatSpreadBps(value);
  if (metric === "fundingHighest") return formatFunding(value);
  if (metric === "fundingVolatility") return formatFundingBps(value);
  return formatPercent(value);
}

function regimeTone(regime: FundingInstabilityRegime | null): string {
  if (regime === "Stable") return "border-border/80 text-muted-foreground";
  if (regime === "Elevated") return "border-primary/50 text-primary";
  if (regime === "Unstable") return "border-amber-400/50 text-amber-300";
  if (regime === "Extreme") return "border-rose-400/50 text-rose-300";
  return "border-border/80 text-muted-foreground";
}

function regimeBar(regime: FundingInstabilityRegime | null): string {
  if (regime === "Stable") return "bg-muted-foreground/70";
  if (regime === "Elevated") return "bg-primary";
  if (regime === "Unstable") return "bg-amber-300";
  if (regime === "Extreme") return "bg-rose-300";
  return "bg-muted-foreground";
}

export function LeaderboardTable({
  title,
  rows,
  valueHeader = "Value",
  subtitle,
  isLoading = false,
  isUpdating = false,
}: LeaderboardTableProps) {
  const navigate = useNavigate();
  const isFundingInstability = rows[0]?.metric === "fundingVolatility";

  return (
    <section className="border border-border/80 bg-card/95">
      <div className="border-b border-border/80 px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs font-mono uppercase tracking-wide text-muted-foreground">{title}</div>
          {isUpdating ? (
            <span className="text-[0.62rem] font-mono uppercase tracking-wide text-primary">
              Updating...
            </span>
          ) : null}
        </div>
        {subtitle ? <div className="mt-1 text-[0.68rem] text-muted-foreground">{subtitle}</div> : null}
      </div>

      {!rows.length ? (
        <p className="px-3 py-4 md:px-5 md:py-6 text-sm text-muted-foreground">
          {isLoading ? "Loading leaderboard..." : "No leaderboard data available."}
        </p>
      ) : (
        <div className={`w-full overflow-x-auto transition-opacity ${isUpdating ? "opacity-70" : "opacity-100"}`}>
          <table className="w-max min-w-full border-collapse">
            <thead className="text-[0.7em] lg:text-xs uppercase font-mono tracking-wide text-muted-foreground">
              <tr className="border-b border-border/70">
                <th className="px-3 py-1.5 md:px-5 md:py-3 text-left">#</th>
                <th className="px-3 py-1.5 md:px-5 md:py-3 text-left">Market</th>
                {isFundingInstability ? (
                  <>
                    <th className="px-3 py-1.5 md:px-5 md:py-3 text-right">Instability</th>
                    <th className="px-3 py-1.5 md:px-5 md:py-3 text-right">Current Funding (ann.)</th>
                    <th className="px-3 py-1.5 md:px-5 md:py-3 text-right">Regime</th>
                  </>
                ) : (
                  <th className="px-3 py-1.5 md:px-5 md:py-3 text-right">{valueHeader}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={`${row.provider}:${row.marketId}:${row.rank}`}
                  role="link"
                  tabIndex={0}
                  onClick={() => navigate(`/markets/${row.provider}/${row.marketId}`)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      navigate(`/markets/${row.provider}/${row.marketId}`);
                    }
                  }}
                  className="cursor-pointer border-b border-border/60 text-xs md:text-sm hover:bg-primary/5"
                >
                  <td className="p-3 lg:px-5 lg:py-4 text-xs text-muted-foreground">#{row.rank}</td>
                  <td className="p-3 lg:px-5 lg:py-4 text-sm">
                    <span className="font-medium">{row.label}</span>
                    <span className="ml-1 text-xs text-muted-foreground uppercase">{row.provider}</span>
                  </td>
                  {isFundingInstability ? (
                    <>
                      <td className="p-3 lg:px-5 lg:py-4 text-right">
                        <div className="flex min-w-[110px] flex-col items-end gap-1">
                          <span className="text-sm font-semibold">{formatFundingBps(row.metricValue)}</span>
                          <span className="h-1 w-20 bg-border/50">
                            <span
                              className={`block h-full ${regimeBar(row.fundingInstabilityRegime)}`}
                              style={{
                                width: `${Math.max(
                                  8,
                                  Math.round((row.fundingInstabilityScore ?? 0) * 100)
                                )}%`,
                              }}
                            />
                          </span>
                        </div>
                      </td>
                      <td className="p-3 lg:px-5 lg:py-4 text-right text-sm font-mono">
                        {typeof row.fundingMean === "number" ? formatFunding(row.fundingMean * 24 * 365) : "—"}
                      </td>
                      <td className="p-3 lg:px-5 lg:py-4 text-right">
                        <span
                          className={`inline-block border px-1.5 py-0.5 text-[0.62rem] font-mono uppercase tracking-wide ${regimeTone(
                            row.fundingInstabilityRegime
                          )}`}
                        >
                          {row.fundingInstabilityRegime ?? "—"}
                        </span>
                      </td>
                    </>
                  ) : (
                    <td className="p-3 lg:px-5 lg:py-4 text-right text-sm font-semibold font-mono">
                      {formatMetric(row.metric, row.metricValue)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
